
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.8. Tint is derived from a stable hash of the identity key so
// the same person keeps the same colour across renders, routes, and sessions —
// a random pick per render makes a queue flicker and destroys recognition.
const TINTS = [
  'bg-brand-100 text-brand-700',
  'bg-info-50 text-info-600',
  'bg-success-50 text-success-700',
  'bg-warning-50 text-warning-700',
  'bg-status-product-background text-status-product-text',
  'bg-surface-subtle text-ink-muted',
] as const;

const SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-base',
  lg: 'h-12 w-12 text-lg',
} as const;

function tintFor(key: string) {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) | 0;
  }
  return TINTS[Math.abs(hash) % TINTS.length];
}

function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface AvatarProps {
  name?: string | null;
  /** Stable identity for colour selection. Falls back to name. */
  id?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

export function Avatar({ name, id, size = 'md', className }: AvatarProps) {
  const label = name?.trim() || 'Unknown';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold',
        SIZES[size],
        tintFor(id || label),
        className,
      )}
      // The name is already rendered as text beside the avatar in every current
      // call site, so exposing it again would double-announce it.
      aria-hidden="true"
      title={label}
    >
      {initialsFor(label)}
    </span>
  );
}
